import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import './EditProfile.css';
import { getStoredProfile } from '../utils/profileStorage';
import { getStoredUser } from '../utils/authStorage';

const buildInitialFormData = () => {
  const storedProfile = getStoredProfile();
  const authUser = getStoredUser();

  return {
    name: storedProfile.name || authUser?.fullName || authUser?.username || '',
    email: storedProfile.email || authUser?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
};

const EditProfile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState(buildInitialFormData);

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, avatar: 'Please select an image file' }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, avatar: 'Image size must be less than 5MB' }));
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
      setErrors(prev => ({ ...prev, avatar: '' }));
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (showPasswordFields) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Current password is required';
      }

      if (formData.newPassword && formData.newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters';
      }

      if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, you would send the data to your backend:
      // const formDataToSend = new FormData();
      // formDataToSend.append('name', formData.name);
      // formDataToSend.append('email', formData.email);
      // if (formData.newPassword) {
      //   formDataToSend.append('currentPassword', formData.currentPassword);
      //   formDataToSend.append('newPassword', formData.newPassword);
      // }
      // if (fileInputRef.current?.files[0]) {
      //   formDataToSend.append('avatar', fileInputRef.current.files[0]);
      // }
      // await axios.put('/api/profile', formDataToSend);

      // For now, just navigate back to profile
      navigate('/profile');
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'Failed to update profile. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  const getInitials = (value) => {
    const source = value?.trim() || formData.email || 'WM';
    return source
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || 'WM';
  };

  return (
    <div className="dashboard-container edit-profile-page">
      <Sidebar />
      <div className="main-content">
        <header className="edit-profile-header">
          <div>
            <p className="eyebrow">Account / Profile / Edit</p>
            <h1>Edit Profile</h1>
            <p className="subheading">
              Update your personal information and account security settings.
            </p>
          </div>
        </header>

        <form className="edit-profile-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="section-header">
              <h2>Profile Picture</h2>
              <p>Upload a new profile picture. Recommended size: 200x200px</p>
            </div>
            <div className="avatar-upload-section">
              <div className="avatar-preview" onClick={handleAvatarClick}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Profile preview" />
                ) : (
                  <div className="avatar-placeholder">
                    {getInitials(formData.name)}
                  </div>
                )}
                <div className="avatar-overlay">
                  <span>Change Photo</span>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              {errors.avatar && <p className="error-text">{errors.avatar}</p>}
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h2>Personal Information</h2>
              <p>Update your name and contact email address.</p>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <p className="error-text">{errors.name}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <p className="error-text">{errors.email}</p>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <div>
                <h2>Password & Security</h2>
                <p>Change your account password to keep your account secure.</p>
              </div>
              {!showPasswordFields && (
                <button
                  type="button"
                  className="ghost-link"
                  onClick={() => setShowPasswordFields(true)}
                >
                  Change Password
                </button>
              )}
            </div>

            {showPasswordFields && (
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="currentPassword">
                    Current Password <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    placeholder="Enter current password"
                    className={errors.currentPassword ? 'error' : ''}
                  />
                  {errors.currentPassword && (
                    <p className="error-text">{errors.currentPassword}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">
                    New Password <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder="At least 8 characters"
                    className={errors.newPassword ? 'error' : ''}
                  />
                  {errors.newPassword && (
                    <p className="error-text">{errors.newPassword}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    Confirm New Password <span className="required">*</span>
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Re-enter new password"
                    className={errors.confirmPassword ? 'error' : ''}
                  />
                  {errors.confirmPassword && (
                    <p className="error-text">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {errors.submit && (
            <div className="form-alert error">
              {errors.submit}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;


